import request from '../../utils/request';
import { Result } from '@/models/general';

// CO-843. Wire contract of Mowazaf.Server/Controllers/TournamentManagement/TournamentSettingsController.cs.
// The company is always the caller's own (taken from the token): nothing here names a company.
// The three modes travel as plain integers in both directions.

/** How often HR receives the tournament summary. */
export const HrSummaryMode = {
  /** On every hourly run. Value 0 is also what a company that never saved gets. */
  Hourly: 0,
  /** Only at the hours listed in `hrSummaryTimes`. */
  AtTimes: 1,
} as const;

/** What HR receives when an official accepts an assignment. */
export const AcceptanceNoticeMode = {
  SummaryOnly: 0,
  InAppPerAcceptance: 1,
  AllChannels: 2,
} as const;

/** What HR receives when assignments expire unanswered. */
export const ExpiryNoticeMode = {
  BatchedPerRun: 0,
  PerAssignment: 1,
} as const;

/** The editable part: exactly the body of `UpdateSettings`. Every hour is a whole hour, 0 to 23, in the company time zone. */
export interface TournamentSettingsInput {
  quietHoursEnabled: boolean;
  quietHoursStartHour: number;
  quietHoursEndHour: number;
  hrSummaryMode: number;
  hrSummaryTimes: number[];
  acceptanceNoticeMode: number;
  expiryNoticeMode: number;
}

export interface TournamentSettingsView extends TournamentSettingsInput {
  /** The company's time zone id, or null when the company has none. */
  companyTimeZone: string | null;
  /** True when the company has no usable time zone, so the server reads every hour as UTC. */
  companyTimeZoneIsFallback: boolean;
}

/** Codes the server attaches to a refused save (`errors[].code`). */
export const TournamentSettingsErrorCode = {
  Required: 'tournamentSettings.required',
  HourOutOfRange: 'tournamentSettings.hourOutOfRange',
  QuietHoursSameStartAndEnd: 'tournamentSettings.quietHoursSameStartAndEnd',
  SummaryTimesRequired: 'tournamentSettings.summaryTimesRequired',
  UnknownOption: 'tournamentSettings.unknownOption',
} as const;

/** The company's tournament settings. A company that never saved gets the defaults. */
export async function getTournamentSettings() {
  const response = await request.get<Result<TournamentSettingsView>>('TournamentSettings/GetSettings');
  return response.data;
}

/** Saves the company's tournament settings and answers the stored values. */
export async function updateTournamentSettings(input: TournamentSettingsInput) {
  const response = await request.put<Result<TournamentSettingsView>>('TournamentSettings/UpdateSettings', input);
  return response.data;
}
